/**
 * What the desktop's first screen offers, decided without rendering anything (F-0518, D-0221 (a)).
 *
 * Cloud-first, like the iPhone app's (F-0333): Create account and Sign in against the deployment
 * this install uses, with no address on the way. "The cloud" is the build's own deployment —
 * `BUILT_IN_DEPLOYMENT_ADDRESS`, featit.com for a production build and dev stable for a `dev-stable`
 * one — and is never written here as a literal (AC-FD2). A deployment this install was pointed at
 * is app data, and outranks it: the first screen then names that deployment, and the cloud moves to
 * the secondary screen (AC-FD4).
 */
import { SIGN_UP_MODES, signUpModeOf, type IFeatitWellKnown } from '@featit/shared/app-setup';
import { signUpClosedCopy } from '@featit/shared/sign-up-closed';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import type { TFn } from '@/i18n/LocaleProvider';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import { onboarding } from '@featit/shared/i18n/messages/en/onboarding';

registerEnglishArea('onboarding', onboarding);
import {
  deviceOpensGetStarted,
  GET_STARTED_PATH,
  isAppStartPath,
} from '@/features/get-started/deviceGetStartedBoot';
import {
  BUILT_IN_DEPLOYMENT_ADDRESS,
  normalizeDeploymentAddress,
  readChosenDeploymentAddress,
} from './deploymentAddress';
import {
  DEPLOYMENT_IDENTITY_FAILURES,
  type DeploymentIdentityFailureType,
} from './deploymentIdentity';

/** Where the desktop's first screen lives. */
export const FIRST_SCREEN_PATH = '/welcome';

export interface IFirstScreenTarget {
  /** The deployment Create account and Sign in act on. */
  address: string;
  /** Whether that is the build's own deployment, which is never named on the first screen. */
  isCloud: boolean;
  /** The build's own deployment, offered on the secondary screen when app data chose another. */
  cloudAddress: string | null;
}

export interface IFirstScreenInputs {
  builtIn: string;
  chosen: string | null;
}

/** Which deployment the first screen is for, or null when this install has none at all. */
export function firstScreenTarget({
  builtIn,
  chosen,
}: IFirstScreenInputs): IFirstScreenTarget | null {
  const cloud = normalizeDeploymentAddress(builtIn);
  const picked = normalizeDeploymentAddress(chosen);
  if (picked && picked !== cloud) return { address: picked, isCloud: false, cloudAddress: cloud };
  if (cloud) return { address: cloud, isCloud: true, cloudAddress: cloud };
  return null;
}

/** This install's first-screen target, read from the build and the stored choice. */
export function currentFirstScreenTarget(): IFirstScreenTarget | null {
  return firstScreenTarget({
    builtIn: BUILT_IN_DEPLOYMENT_ADDRESS,
    chosen: readChosenDeploymentAddress(),
  });
}

/**
 * Where a signed-out window is sent: the first screen on the desktop, the sign-in form in a browser
 * tab, which a person reached by opening one deployment's own address (AC-FD1).
 *
 * F-0581 OB-1: ahead of both, Get started, when the window opens the app itself (`pathname` is an
 * app start, not a link to a page) on a device that has not finished it.
 */
export function signedOutEntryPath(pathname?: string): string {
  if (pathname !== undefined && isAppStartPath(pathname) && deviceOpensGetStarted()) {
    return GET_STARTED_PATH;
  }
  return isDesktopRuntime() ? FIRST_SCREEN_PATH : '/login';
}

/** One title per failure, so no two of them read alike (AC-FD6). The keys of the `onboarding` area. */
export const IDENTITY_FAILURE_TITLES: Record<DeploymentIdentityFailureType, MessageKey> = {
  [DEPLOYMENT_IDENTITY_FAILURES.offline]: 'onboarding.failTitle.offline',
  [DEPLOYMENT_IDENTITY_FAILURES.unreachable]: 'onboarding.failTitle.unreachable',
  [DEPLOYMENT_IDENTITY_FAILURES.notFeatit]: 'onboarding.failTitle.notFeatit',
  [DEPLOYMENT_IDENTITY_FAILURES.certificate]: 'onboarding.failTitle.certificate',
};

/** What each failure asks the person to check. */
export const IDENTITY_FAILURE_DETAILS: Record<DeploymentIdentityFailureType, MessageKey> = {
  [DEPLOYMENT_IDENTITY_FAILURES.offline]: 'onboarding.failDetail.offline',
  [DEPLOYMENT_IDENTITY_FAILURES.unreachable]: 'onboarding.failDetail.unreachable',
  [DEPLOYMENT_IDENTITY_FAILURES.notFeatit]: 'onboarding.failDetail.notFeatit',
  [DEPLOYMENT_IDENTITY_FAILURES.certificate]: 'onboarding.failDetail.certificate',
};

export interface IFirstScreenSignUp {
  /** Whether Create account is offered at all (AC-FD5). */
  offersCreateAccount: boolean;
  /** What to do instead, when it is not. */
  note: string | null;
  /** T-0615, AC-W19c: the early-access form a closed deployment points at, when it names one. */
  earlyAccessUrl: string | null;
}

/**
 * Create account only on a deployment that takes sign-ups. Before its identity has answered, both
 * actions are offered: hiding one and then showing it moves the screen under the person's pointer,
 * and the sign-up form asks the deployment again anyway.
 */
export function firstScreenSignUp(identity: IFeatitWellKnown | null, t: TFn): IFirstScreenSignUp {
  if (!identity) return { offersCreateAccount: true, note: null, earlyAccessUrl: null };
  const mode = signUpModeOf(identity);
  if (mode === SIGN_UP_MODES.INVITATION) {
    return {
      offersCreateAccount: false,
      note: t('onboarding.invitationNote'),
      earlyAccessUrl: null,
    };
  }
  if (mode === SIGN_UP_MODES.CLOSED) {
    // T-0615, AC-W19c: the public cloud before it opens names early access; a server, its operator.
    const copy = signUpClosedCopy(identity.earlyAccessUrl ?? null);
    return { offersCreateAccount: false, note: copy.body, earlyAccessUrl: copy.earlyAccessUrl };
  }
  return { offersCreateAccount: true, note: null, earlyAccessUrl: null };
}
